import { ControlElement, EnumOption } from "@jsonforms/core";
import { useFocus } from "@jsonforms/material-renderers";
import {
  Autocomplete,
  Box,
  FormHelperText,
  styled,
  TextField,
} from "@mui/material";
import { Constants, debounce, useJsonFormErrors } from "hhn-commons";
import { merge } from "lodash";
import {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

export type JsonFormsAutocompleteHandle = {
  clear: (initialOptions: EnumOption[]) => void;
};

export type JsonFormsAutocompleteProps = {
  id?: string;
  initialOptions: EnumOption[];
  initialOption?: EnumOption;
  renderOption?: (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: EnumOption,
  ) => ReactNode;
  label: string;
  uischema: ControlElement;
  required?: boolean;
  data: any;
  path: string;
  enabled: boolean;
  helperText?: string;
  handleChange(path: string, value: any): void;
  config?: any;
  options?: EnumOption[];
  onInputChange?: (inputValue: string) => void;
  loading?: boolean;
};

export const JsonFormsAutocomplete = forwardRef<
  JsonFormsAutocompleteHandle,
  JsonFormsAutocompleteProps
>(
  (
    {
      id,
      initialOptions,
      initialOption,
      renderOption,
      label,
      uischema,
      required,
      data,
      path,
      enabled,
      helperText,
      handleChange,
      config,
      options: externalOptions,
      onInputChange: externalOnInputChange,
      loading: externalLoading,
    },
    ref,
  ) => {
    const [internalLoading, setInternalLoading] = useState(false);
    const { isValid } = useJsonFormErrors(path);

    const [allOptions, setAllOptions] = useState<EnumOption[]>(initialOptions);
    const [filteredOptions, setFilteredOptions] =
      useState<EnumOption[]>(initialOptions);
    const [value, setValue] = useState(
      initialOption || { label: "", value: null },
    );
    const [inputValue, setInputValue] = useState(initialOption?.label || "");
    const appliedUiSchemaOptions = merge({}, config, uischema.options);
    const [focused, onFocus, onBlur] = useFocus();

    useImperativeHandle(ref, () => ({
      clear: (initialOptions: EnumOption[]) => {
        setValue({ label: "", value: null });
        setInputValue("");
        setAllOptions(initialOptions);
        setFilteredOptions(initialOptions);
      },
    }));

    const internalHandleInputChange = useCallback(
      async (inputValue: string) => {
        if (!inputValue) {
          setFilteredOptions(allOptions);
          return;
        }

        if (allOptions.length === 0) {
          setFilteredOptions([]);
          return;
        }

        setInternalLoading(true);
        const searchText = inputValue.toLowerCase();
        const filtered = allOptions.filter((item: EnumOption) => {
          const name = (item.label || "").toLowerCase();
          return name.includes(searchText);
        });
        setFilteredOptions(filtered);
        setInternalLoading(false);
      },
      [allOptions],
    );

    const handleInputChangeWrapper = useCallback(
      (newInputValue: string) => {
        setInputValue(newInputValue);
        if (externalOnInputChange) {
          externalOnInputChange(newInputValue);
        } else {
          internalHandleInputChange(newInputValue);
        }
      },
      [externalOnInputChange, internalHandleInputChange],
    );

    const displayOptions = externalOptions ?? filteredOptions;
    const isLoading = externalLoading ?? internalLoading;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedChange = useCallback(
      debounce((nextInputText: string | null) => {
        handleChange(path, nextInputText);
      }, Constants.API_DEBOUNCE_DELAY_IN_MS),
      [],
    );

    const onValueChange = (option: EnumOption | null) => {
      debouncedChange(option?.value);
      setValue(option || { label: "", value: null });
    };

    return (
      <RootContainer>
        <Autocomplete
          id={id}
          value={value}
          onChange={(event: any, newValue: EnumOption | null) => {
            onValueChange(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            handleInputChangeWrapper(newInputValue);
          }}
          options={displayOptions}
          renderOption={renderOption}
          filterOptions={x => x}
          renderInput={params => (
            <TextField
              label={label}
              autoFocus={appliedUiSchemaOptions?.focus}
              {...params}
              id={id}
              required={
                required && !appliedUiSchemaOptions?.hideRequiredAsterisk
              }
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  type: "text",
                },

                inputLabel: data ? { shrink: true } : undefined
              }}
              error={!isValid}
              fullWidth={!appliedUiSchemaOptions?.trim}
              onFocus={onFocus}
              onBlur={onBlur}
              focused={focused}
            />
          )}
          slotProps={{
            listbox: {
              key: `${displayOptions.length}-${displayOptions[0]?.value || ""}-${displayOptions[displayOptions.length - 1]?.value || ""}`,
            },
          }}
          loading={isLoading}
          disabled={!enabled}
        />
        {helperText && (
          <FormHelperText error={false} style={{ position: "absolute" }}>
            {helperText}
          </FormHelperText>
        )}
      </RootContainer>
    );
  },
);

const RootContainer = styled(Box)`
  position: relative;
  padding-bottom: 5px;
  padding-top: 5px;
`;
